import { useCallback } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Empty, Spin } from 'antd';

import { ListOvrProjectModel } from '../../../../../models/online-virtual-research';
import { useAppDispatch } from '../../../../../store';
import { loadMoreOvrProjects } from '../../../../../store/features/ovrProjects/ovrProjectsSlice';
import NoResultsFound from '../../../../elements/NoResultsFound';
import CreateNewProjectButton from '../CreateNewProjectButton';
import OVRProjectItemsList, {
  OVRItemsListViewType,
} from '../OVRProjectItemsList';

const antSpinIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;

interface OVRContentRendererProps {
  searchingOvrProjects: boolean;
  hasNoProjects: boolean;
  hasNoSearchResultsFound: boolean;
  hasMoreOvrProjects: boolean;
  isPermittedToLoadMore: boolean;
  fetchingOvrProjects: boolean;
  ovrProjects: ListOvrProjectModel[];
  viewType: OVRItemsListViewType;
  search: string;
  onSearchClear: () => void;
}

const OVRContentRenderer = ({
  searchingOvrProjects,
  hasNoProjects,
  hasNoSearchResultsFound,
  hasMoreOvrProjects,
  isPermittedToLoadMore,
  fetchingOvrProjects,
  ovrProjects,
  viewType,
  search,
  onSearchClear,
}: OVRContentRendererProps) => {
  const dispatch = useAppDispatch();

  const handleLoadMore = useCallback(() => {
    dispatch(loadMoreOvrProjects());
  }, [dispatch]);

  const renderContent = () => {
    if (searchingOvrProjects) {
      return (
        <div className="custom-fields-view-container">
          <Spin indicator={antSpinIcon} />
        </div>
      );
    }

    if (!hasNoProjects) {
      return (
        <OVRProjectItemsList
          skeletonItemLength={
            +localStorage.getItem(
              'last-ovr-projects-item-length-key'
            )! as number
          }
          viewType={viewType}
          loading={fetchingOvrProjects}
          projects={ovrProjects}
          onLoadMore={handleLoadMore}
          hasMore={isPermittedToLoadMore}
        />
      );
    }

    if (hasNoSearchResultsFound) {
      return <NoResultsFound searchQuery={search} onClear={onSearchClear} />;
    }

    if (!hasMoreOvrProjects) {
      return (
        <div className="custom-fields-view-container">
          <Empty
            description="Currently no ovr projects."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <CreateNewProjectButton />
          </Empty>
        </div>
      );
    }
  };

  return <>{renderContent()}</>;
};

export default OVRContentRenderer;
